<template>
  <f7-sheet
    class="supplier-source-post-sheet"
    :opened="opened"
    backdrop
    close-by-backdrop-click
    @sheet:closed="onClosed"
  >
    <f7-toolbar>
      <div class="left" />
      <div class="center">{{ $t('supplier.dailyOrders.item.viewPostAndPhotos') }}</div>
      <div class="right">
        <f7-link
          sheet-close
          class="supplier-source-post-sheet__close"
          :aria-label="$t('supplier.dailyOrders.sheet.closeAria')"
        >
          <f7-icon f7="xmark" size="20"></f7-icon>
        </f7-link>
      </div>
    </f7-toolbar>

    <f7-page-content>
      <template v-if="item">
        <p v-if="postContent" class="supplier-source-post-sheet__text">{{ postContent }}</p>

        <p v-if="demandCount > 0" class="supplier-source-post-sheet__demand">
          {{ $t('supplier.dailyOrders.sheet.demandCountLabel', { count: demandCount }) }}
        </p>

        <div v-if="photoUrls.length" class="supplier-source-post-sheet__grid">
          <button
            v-for="(url, index) in photoUrls"
            :key="`${url}-${index}`"
            type="button"
            class="supplier-source-post-sheet__thumb"
            @click="openGallery(index)"
          >
            <img :src="url" :alt="itemName" loading="lazy" />
          </button>
        </div>
      </template>
    </f7-page-content>
  </f7-sheet>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, onUnmounted, shallowRef } from 'vue';

const props = defineProps({
  // The SupplierDailyOrderItem (or plain object) whose source post is shown.
  // null while the sheet is closed / before any card has been tapped.
  item: {
    type: Object,
    default: null,
  },
  // v-model:opened — the page owns which item is loaded + drives this flag.
  opened: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:opened']);

const itemName = computed(() => props.item?.productName || '');
const postContent = computed(() => props.item?.postContent || '');
const demandCount = computed(() => Number(props.item?.demandCount) || 0);

// Same defensive fallback as SupplierDailyOrderItemCard — tolerates a plain
// fixture/mock object that doesn't carry the model's own `photoUrls` getter.
const photoUrls = computed(() => {
  const { item } = props;
  if (!item) return [];
  if (Array.isArray(item.photoUrls)) return item.photoUrls;
  const gallery = (Array.isArray(item.catalogPhotos) ? item.catalogPhotos : [])
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object') return entry.url ?? entry.src ?? entry.href ?? null;
      return null;
    })
    .filter(Boolean);
  if (gallery.length) return gallery;
  return item.photoUrl ? [item.photoUrl] : [];
});

// Imperative F7 PhotoBrowser — mirrors DemandPostCard.vue's openGallery exactly.
// <f7-photo-browser> is headless (render() returns null); this is the app's
// only real pattern for a full-screen photo view.
const pbInstance = shallowRef(null);
function openGallery(startIndex = 0) {
  const photos = photoUrls.value;
  if (!photos.length) return;
  const validIndex = Math.max(0, Math.min(startIndex, photos.length - 1));
  try {
    if (pbInstance.value) {
      pbInstance.value.destroy();
      pbInstance.value = null;
    }
    pbInstance.value = f7.photoBrowser.create({
      photos: photos.map((url) => ({ url })),
      type: 'standalone',
      toolbar: true,
      theme: 'dark',
    });
    pbInstance.value.open(validIndex);
  } catch (err) {
    console.error('[SupplierSourcePostSheet] photo browser failed:', err);
  }
}

function destroyPhotoBrowser() {
  if (pbInstance.value) {
    pbInstance.value.destroy();
    pbInstance.value = null;
  }
}

// The sheet is a persistent, page-level singleton reused across many
// open/close cycles (one per card tapped) — the photo browser must be
// destroyed on EVERY close, not just on this component's own unmount.
function onClosed() {
  destroyPhotoBrowser();
  if (props.opened) emit('update:opened', false);
}

onUnmounted(destroyPhotoBrowser);
</script>

<style scoped>
.supplier-source-post-sheet {
  height: 85%;
}

.supplier-source-post-sheet .center {
  font-weight: var(--poolit-font-weight-semibold, 600);
}

.supplier-source-post-sheet__close {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
}

.supplier-source-post-sheet__text {
  margin: 12px 16px;
  color: var(--f7-text-color);
  font-size: 15px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.supplier-source-post-sheet__demand {
  margin: 0 16px 12px;
  color: var(--poolit-text-secondary, #374151);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.supplier-source-post-sheet__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin: 0 16px 16px;
}

.supplier-source-post-sheet__thumb {
  padding: 0;
  border: 0;
  border-radius: var(--poolit-radius-md, 8px);
  overflow: hidden;
  aspect-ratio: 1;
  background: var(--f7-color-gray-100, #f1f3f5);
  cursor: pointer;
}

.supplier-source-post-sheet__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.dark .supplier-source-post-sheet__thumb {
  background: var(--f7-color-gray-800, #1f2937);
}
</style>
